'use client';

/**
 * Keystroke guard — puts back letters that vanish while you type.
 *
 * Field report, twice now: "FlowStage deletes letters from words while I am
 * typing." Nothing in the app's own form code explains it (the inline editors
 * hold local drafts, the pickers never rewrite their own input, auth refresh
 * deliberately avoids a re-render storm), and it has never reproduced on
 * demand. The usual suspects live outside our code: a writing-assistant
 * extension rewriting the field under React, a save round-trip landing on a
 * value the person has since typed past, a re-render restoring a stale value.
 *
 * So rather than guess at a cause, defend against the SHAPE of it. While a
 * field is focused, this watches for the value changing to a shorter prefix of
 * what the person actually typed, within a moment of their last keystroke, with
 * no input event of their own to explain it. That is characters being taken
 * away mid-word. It puts the text and the caret back, and reports where it
 * happened so the cause stops being a mystery.
 *
 * Deliberately narrow:
 *   - Only a strict, shorter prefix of the typed text is restored. Anything
 *     else (a clear, a reformat, a reset to a different value) is left alone —
 *     those are usually the app doing its job.
 *   - Trailing-whitespace-only differences are ignored, so a field that trims
 *     as you type is not fought.
 *   - At most a few restores per field, rate-limited, so a normaliser and this
 *     guard can never ping-pong.
 *   - Password fields are never read or touched.
 */

import { useEffect } from 'react';
import { trackEvent } from '@/lib/mixpanel';

type Field = HTMLInputElement | HTMLTextAreaElement;

const WATCHED_INPUT_TYPES = new Set(['text', 'search', 'email', 'tel', 'url', 'number', '']);
/** A change this long after the last keystroke is the app working, not a clobber. */
const TYPING_WINDOW_MS = 1500;
/** Never restore the same field twice in quick succession. */
const RESTORE_COOLDOWN_MS = 800;
const MAX_RESTORES_PER_FIELD = 5;

function isWatched(el: EventTarget | null): el is Field {
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return false;
  if (el instanceof HTMLInputElement) {
    if (el.type === 'password') return false;
    if (!WATCHED_INPUT_TYPES.has(el.type)) return false;
  }
  return true;
}

/** Set a value the way React notices — through the native setter, then an input event. */
function setValueThroughReact(el: Field, value: string) {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

/** A label a human can act on: "Fee", "Search contractors…", or the field name. */
function describeField(el: Field): string {
  const labelled = el.getAttribute('aria-label');
  if (labelled) return labelled;
  const id = el.getAttribute('id');
  if (id) {
    const label = document.querySelector(`label[for="${CSS.escape(id)}"]`);
    if (label?.textContent) return label.textContent.trim().slice(0, 60);
  }
  return el.getAttribute('placeholder') || el.getAttribute('name') || el.tagName.toLowerCase();
}

export default function TypingGuard() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let field: Field | null = null;
    let typed = '';
    let lastKeyAt = 0;
    let lastRestoreAt = 0;
    let restores = 0;
    let frame = 0;

    const onInput = (e: Event) => {
      if (!isWatched(e.target) || e.target !== field) return;
      typed = field.value;
      lastKeyAt = Date.now();
    };

    const check = () => {
      frame = requestAnimationFrame(check);
      if (!field || document.activeElement !== field) return;

      const now = Date.now();
      const current = field.value;
      if (current === typed) return;
      // Outside the typing window this is the app legitimately setting a value.
      if (now - lastKeyAt > TYPING_WINDOW_MS) { typed = current; return; }

      // Two or more, deliberately. A field that rejects the character you just
      // typed (digits-only, a formatter) drops exactly one and would otherwise
      // be fought on every keystroke; losing a run of characters is the
      // reported bug.
      const lostCharacters = typed.startsWith(current) && typed.length - current.length >= 2;
      const trailingSpaceOnly = typed.trimEnd() === current.trimEnd();
      // A field enforcing its own maximum is not losing characters, it is doing
      // what it says. Restoring there would flicker against the limit.
      const atItsLimit = field.maxLength >= 0 && current.length >= field.maxLength;

      if (!lostCharacters || trailingSpaceOnly || atItsLimit || current.length === 0) {
        // Not our shape — record it (this is how we learn the real cause) but
        // never fight the app over it.
        if (!trailingSpaceOnly) {
          trackEvent('Typing Value Changed Under User', {
            page: window.location.pathname,
            field: describeField(field),
            typed_length: typed.length,
            shown_length: current.length,
            restored: false,
          });
        }
        typed = current;
        return;
      }

      if (restores >= MAX_RESTORES_PER_FIELD || now - lastRestoreAt < RESTORE_COOLDOWN_MS) {
        typed = current;
        return;
      }

      const caret = typed.length;
      restores += 1;
      lastRestoreAt = now;
      const el = field;
      setValueThroughReact(el, typed);
      try { el.setSelectionRange(caret, caret); } catch { /* number inputs refuse a range */ }

      trackEvent('Typing Value Changed Under User', {
        page: window.location.pathname,
        field: describeField(el),
        typed_length: typed.length,
        shown_length: current.length,
        restored: true,
      });
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[TypingGuard] restored characters removed mid-typing', {
          field: describeField(el), typed, showed: current,
        });
      }
    };

    const onFocusIn = (e: FocusEvent) => {
      if (!isWatched(e.target)) return;
      field = e.target;
      typed = field.value;
      lastKeyAt = 0;
      restores = 0;
      lastRestoreAt = 0;
      if (!frame) frame = requestAnimationFrame(check);
    };

    const onFocusOut = () => {
      field = null;
      if (frame) { cancelAnimationFrame(frame); frame = 0; }
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('input', onInput, true);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('input', onInput, true);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return null;
}
